/**
 * WhatsApp voice calls in the browser (Cloud API Calling).
 *
 * Module singleton, like notification-ws — no React dependency. The agent's
 * browser is the WebRTC endpoint and Meta's media server the peer, so audio
 * never touches Setu. Signaling:
 *   - REST: our SDP offer/answer → backend → Meta
 *   - notification socket: `call.incoming` · `call.claimed` · `call.answer`
 *     · `call.status` · `call.ended` (see handleCallEvent)
 *
 * One call per tab. The mic, peer connection and ring tone exist only while a
 * call rings or runs; everything is released on hang-up.
 */

import { toast } from 'sonner'
import { callsApi, type CallSocketPayload } from '@/api/endpoints/calls'
import { errorMessage } from '@/lib/api-messages'
import { useAuthStore } from '@/stores/authStore'
import { useCallStore, type ActiveCall } from '@/stores/callStore'
import { savedRingtone } from '@/stores/ringtoneStore'
import { playRingback, playRingtone } from './ringtones'

/** STUN only adds the server-reflexive candidate; Meta's side is public. */
const ICE_SERVERS: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }]
/** Meta needs the full SDP (no trickle ICE); cap the gathering wait. */
const ICE_GATHER_TIMEOUT_MS = 2500
/** Meta drops an unanswered call after ~30-60s; a lost `call.ended` must
 *  not leave the popup ringing forever. */
const RING_TIMEOUT_MS = 75_000
/** An agent's outgoing call nobody answers ends after this long. Also the
 *  safety net when Meta's "declined"/"ended" events never arrive. */
const OUTBOUND_RING_TIMEOUT_MS = 60_000
/** How long the audio link may stay "disconnected" before the call is
 *  treated as over (the other side is gone, its end event lost). */
const MEDIA_DROP_GRACE_MS = 5000

let pc: RTCPeerConnection | null = null
let mic: MediaStream | null = null
let remoteAudio: HTMLAudioElement | null = null
/** SDP answer that raced ahead of the start request's response. */
let pendingAnswer: { id: string; sdp: string } | null = null
/** "accepted"/"rejected" that raced ahead of the start request's response. */
let pendingStatus: { id: string; status: string } | null = null
let noAnswerTimer: ReturnType<typeof setTimeout> | null = null
let mediaDropTimer: ReturnType<typeof setTimeout> | null = null
/** Set by the overlay from RBAC — agents who can't answer never ring. */
let canAnswer = false

// ─── Ring tone (agent's choice, see ringtones.ts) ─────────────────────
//
// Every open Setu tab gets the ring event and shows the call card, but only
// ONE tab per browser plays the sound — otherwise several tabs ring at once,
// out of sync (and an old tab with an older tone rings over the new one).
// The tab holding the Web Lock below plays it. First claim goes to a tab
// that can actually make sound (browsers allow it only once the page has
// been clicked or typed in), visible before hidden. "Silence" in any tab
// stops it everywhere via a BroadcastChannel.

const RING_LOCK = 'setu-call-ring'
/** A hidden tab waits this long so a visible tab can take the ring first. */
const HIDDEN_TAB_DELAY_MS = 400
/** A tab never clicked can't play sound — it claims last, as a fallback. */
const SILENT_TAB_DELAY_MS = 900

let stopTone: (() => void) | null = null
let releaseRingLock: (() => void) | null = null
/** Call this tab is ringing for (null = not ringing). */
let ringingId: string | null = null
let ringTimeout: ReturnType<typeof setTimeout> | null = null
let savedTitle: string | null = null

const ringChannel: BroadcastChannel | null =
  typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('setu-call-ring')

if (ringChannel) {
  ringChannel.onmessage = (event: MessageEvent<{ type?: string; id?: string }>) => {
    if (event.data?.type === 'silence' && event.data.id && event.data.id === ringingId) {
      muteTone()
    }
  }
}

function muteTone() {
  stopTone?.()
  stopTone = null
  releaseRingLock?.()
  releaseRingLock = null
  forgetGestureRetry()
  useCallStore.getState().setRingSilenced(true)
}

/** Plays the agent's CURRENT ringtone (read fresh — it may have been
 *  changed in another tab since this one loaded). */
function startTone() {
  stopTone = playRingtone(savedRingtone(), { onBlocked: onSoundBlocked })
}

/** The browser refused sound in this tab: say so on the card and start the
 *  ringtone on the agent's first click or key press anywhere in Setu. */
function onSoundBlocked() {
  useCallStore.getState().setRingBlocked(true)
  window.addEventListener('pointerdown', retryAfterGesture, true)
  window.addEventListener('keydown', retryAfterGesture, true)
}

function retryAfterGesture(event: Event) {
  // A click on the call card itself is the agent acting on the call —
  // don't start the sound just as they accept or decline.
  if ((event.target as Element | null)?.closest?.('[data-call-card]')) return
  forgetGestureRetry()
  if (!ringingId || !stopTone) return
  stopTone()
  useCallStore.getState().setRingBlocked(false)
  startTone()
}

function forgetGestureRetry() {
  window.removeEventListener('pointerdown', retryAfterGesture, true)
  window.removeEventListener('keydown', retryAfterGesture, true)
}

/** How long this tab waits before claiming the ring. */
function ringClaimDelay(): number {
  const canPlaySound = navigator.userActivation?.hasBeenActive ?? true
  if (!canPlaySound) return SILENT_TAB_DELAY_MS
  return document.visibilityState === 'visible' ? 0 : HIDDEN_TAB_DELAY_MS
}

/** Play the ringtone if this tab wins the browser-wide ring lock. */
async function claimRing(callId: string) {
  const delay = ringClaimDelay()
  if (delay) await new Promise((resolve) => setTimeout(resolve, delay))
  if (ringingId !== callId) return
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
  if (!locks) {
    // No Web Locks (very old browser): ring here, as before.
    startTone()
    return
  }
  try {
    await locks.request(RING_LOCK, { ifAvailable: true }, async (lock) => {
      // Another tab is already ringing, or the call stopped meanwhile.
      if (!lock || ringingId !== callId) return
      startTone()
      // Hold the lock until this tab stops ringing.
      await new Promise<void>((resolve) => {
        releaseRingLock = resolve
      })
    })
  } catch {
    // Lock API refused (e.g. insecure context) — the card still shows.
  }
}

function startRinging(callId: string, onTimeout: () => void) {
  stopRinging()
  ringingId = callId
  void claimRing(callId)
  savedTitle = document.title
  document.title = '📞 Incoming call · Setu'
  ringTimeout = setTimeout(onTimeout, RING_TIMEOUT_MS)
}

function stopRinging() {
  ringingId = null
  stopTone?.()
  stopTone = null
  releaseRingLock?.()
  releaseRingLock = null
  forgetGestureRetry()
  if (ringTimeout) clearTimeout(ringTimeout)
  ringTimeout = null
  if (savedTitle !== null) {
    document.title = savedTitle
    savedTitle = null
  }
}

// ─── WebRTC ──────────────────────────────────────────────────────────

function warnOnUnload(event: BeforeUnloadEvent) {
  event.preventDefault()
}

async function openPeer(): Promise<RTCPeerConnection> {
  mic = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  })
  const peer = new RTCPeerConnection({ iceServers: ICE_SERVERS })
  pc = peer
  mic.getTracks().forEach((track) => peer.addTrack(track, mic as MediaStream))
  peer.ontrack = (event) => {
    remoteAudio ??= new Audio()
    remoteAudio.autoplay = true
    remoteAudio.srcObject = event.streams[0] ?? new MediaStream([event.track])
    void remoteAudio.play().catch(() => {})
  }
  peer.onconnectionstatechange = () => {
    if (pc !== peer) return
    const state = peer.connectionState
    if (state === 'connected') {
      clearMediaDropTimer()
    } else if (state === 'failed' || state === 'closed') {
      endOnMediaLoss()
    } else if (state === 'disconnected' && !mediaDropTimer) {
      // Brief network blips recover on their own; a hung-up peer doesn't.
      mediaDropTimer = setTimeout(() => {
        mediaDropTimer = null
        if (pc === peer && peer.connectionState === 'disconnected') endOnMediaLoss()
      }, MEDIA_DROP_GRACE_MS)
    }
  }
  window.addEventListener('beforeunload', warnOnUnload)
  return peer
}

/** Local SDP once ICE gathering is complete (or the timeout hits). */
function gatheredSdp(peer: RTCPeerConnection): Promise<string> {
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer)
      peer.removeEventListener('icegatheringstatechange', check)
      resolve(peer.localDescription?.sdp ?? '')
    }
    const check = () => {
      if (peer.iceGatheringState === 'complete') done()
    }
    const timer = setTimeout(done, ICE_GATHER_TIMEOUT_MS)
    peer.addEventListener('icegatheringstatechange', check)
    check()
  })
}

// ─── Ringback (agent's own outgoing call) ────────────────────────────

let stopRingback: (() => void) | null = null

/** "Tring-tring" while the customer's phone rings — Meta sends no audio
 *  before they answer. At the agent's ringtone volume (Settings → Calling). */
function startRingback() {
  stopRingbackTone()
  stopRingback = playRingback(savedRingtone().volume)
}

function stopRingbackTone() {
  stopRingback?.()
  stopRingback = null
}

function clearMediaDropTimer() {
  if (mediaDropTimer) clearTimeout(mediaDropTimer)
  mediaDropTimer = null
}

function releaseMedia() {
  stopRingbackTone()
  clearMediaDropTimer()
  if (noAnswerTimer) clearTimeout(noAnswerTimer)
  noAnswerTimer = null
  pendingStatus = null
  window.removeEventListener('beforeunload', warnOnUnload)
  pc?.close()
  pc = null
  mic?.getTracks().forEach((track) => track.stop())
  mic = null
  if (remoteAudio) {
    remoteAudio.srcObject = null
    remoteAudio = null
  }
  pendingAnswer = null
}

function endActive() {
  releaseMedia()
  useCallStore.getState().setActive(null)
}

function micError(err: unknown, fallback: string): string {
  const name = (err as { name?: string } | null)?.name
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Allow microphone access in your browser to make and take calls.'
  }
  if (name === 'NotFoundError') return 'No microphone found on this device.'
  return errorMessage(err, fallback)
}

/** The audio side of an outgoing call. Meta sends this SDP answer as soon
 *  as the call is placed — NOT when the customer picks up (that is the
 *  "accepted" status) — so the call stays "Ringing…" with its ringback. */
async function applyAnswer(sdp: string) {
  const peer = pc
  if (!peer) return
  try {
    await peer.setRemoteDescription({ type: 'answer', sdp })
  } catch {
    toast.error("Couldn't connect the call audio.")
    hangUp()
  }
}

// ─── Outgoing call progress ─────────────────────────────────────────

/** The customer picked up: start the timer, drop the ringback. */
function markAnswered() {
  const active = useCallStore.getState().active
  if (!active || active.status === 'ongoing') return
  stopRingbackTone()
  if (noAnswerTimer) clearTimeout(noAnswerTimer)
  noAnswerTimer = null
  useCallStore.getState().patchActive({ status: 'ongoing', startedAt: Date.now() })
}

/** Meta's ringing / accepted / rejected for the call this tab placed. */
function applyOutboundStatus(status: string) {
  const store = useCallStore.getState()
  if (status === 'ringing' && store.active?.status === 'connecting') {
    store.patchActive({ status: 'ringing' })
  } else if (status === 'accepted') {
    markAnswered()
  } else if (status === 'rejected') {
    // End now — don't keep ringing until the "ended" event (it may be late).
    endActive()
    toast('Call declined.')
  }
}

/** Customer audio arriving means they picked up, even if Meta's
 *  "accepted" event got lost. */
async function customerAudioFlowing(): Promise<boolean> {
  try {
    const stats = await pc?.getStats()
    let energy = 0
    stats?.forEach((report: { type?: string; kind?: string; totalAudioEnergy?: number }) => {
      if (report.type === 'inbound-rtp' && report.kind === 'audio') {
        energy += report.totalAudioEnergy ?? 0
      }
    })
    return energy > 0.01
  } catch {
    return false
  }
}

/** Still ringing after OUTBOUND_RING_TIMEOUT_MS: nobody answered — or the
 *  pickup event was lost while the customer is already talking. */
async function onNoAnswer(callId: string) {
  noAnswerTimer = null
  const active = useCallStore.getState().active
  if (!active || active.id !== callId || active.status === 'ongoing') return
  if (await customerAudioFlowing()) {
    markAnswered()
    return
  }
  hangUp()
  toast('No answer.')
}

/** The audio link is gone for good — the other side hung up (its "ended"
 *  event may never arrive). Hang up so Meta stops billing too. */
function endOnMediaLoss() {
  const active = useCallStore.getState().active
  if (!active) return
  hangUp()
  toast(active.status === 'ongoing' ? 'Call ended.' : "Call didn't connect.")
}

// ─── Public actions ─────────────────────────────────────────────────

/** RBAC gate, set by the overlay (agents without reply rights never ring). */
export function setCanAnswer(value: boolean) {
  canAnswer = value
}

export async function acceptIncoming() {
  const store = useCallStore.getState()
  const call = store.incoming
  if (!call || store.active) return
  stopRinging()
  store.setIncoming(null)
  store.setActive({
    id: call.id,
    wabaId: call.wabaId,
    direction: 'inbound',
    customerId: call.customerId,
    customerName: call.customerName,
    waId: call.waId,
    status: 'connecting',
    startedAt: null,
    muted: false,
  })
  try {
    const peer = await openPeer()
    await peer.setRemoteDescription({ type: 'offer', sdp: call.sdp })
    await peer.setLocalDescription(await peer.createAnswer())
    const sdp = await gatheredSdp(peer)
    await callsApi.accept(call.wabaId, call.id, sdp)
    useCallStore.getState().patchActive({ status: 'ongoing', startedAt: Date.now() })
  } catch (err) {
    endActive()
    toast.error(micError(err, "Couldn't answer the call."))
  }
}

/** Stop the ring sound but keep the card — answer or decline later. */
export function silenceRinging() {
  const id = ringingId
  muteTone()
  // The sound may be playing in another tab — tell every tab to stop.
  if (id) ringChannel?.postMessage({ type: 'silence', id })
}

export function rejectIncoming() {
  const call = useCallStore.getState().incoming
  if (!call) return
  stopRinging()
  useCallStore.getState().setIncoming(null)
  callsApi.reject(call.wabaId, call.id).catch((err) => {
    toast.error(errorMessage(err, "Couldn't decline the call."))
  })
}

export async function startCall(params: {
  wabaId: string
  customerId: number
  customerName: string
  waId: string
}) {
  const store = useCallStore.getState()
  if (store.active) {
    toast.error('Finish the current call first.')
    return
  }
  const draft: ActiveCall = {
    id: null,
    wabaId: params.wabaId,
    direction: 'outbound',
    customerId: params.customerId,
    customerName: params.customerName,
    waId: params.waId,
    status: 'connecting',
    startedAt: null,
    muted: false,
  }
  store.setActive(draft)
  try {
    const peer = await openPeer()
    await peer.setLocalDescription(await peer.createOffer())
    const sdp = await gatheredSdp(peer)
    const call = await callsApi.start(params.wabaId, params.customerId, sdp)
    if (useCallStore.getState().active !== draft || pc !== peer) {
      // Agent hung up while the call was being placed.
      callsApi.terminate(params.wabaId, call.id).catch(() => {})
      return
    }
    useCallStore.getState().patchActive({ id: call.id, status: 'ringing' })
    startRingback()
    noAnswerTimer = setTimeout(() => void onNoAnswer(call.id), OUTBOUND_RING_TIMEOUT_MS)
    if (pendingAnswer?.id === call.id) await applyAnswer(pendingAnswer.sdp)
    if (pendingStatus?.id === call.id) applyOutboundStatus(pendingStatus.status)
  } catch (err) {
    if (useCallStore.getState().active === draft) endActive()
    toast.error(micError(err, "Couldn't place the call."))
  }
}

export function hangUp() {
  const active = useCallStore.getState().active
  if (!active) return
  if (active.id !== null) {
    // Meta bills until it receives terminate, even after media stops.
    callsApi.terminate(active.wabaId, active.id).catch(() => {})
  }
  endActive()
}

export function toggleMute() {
  const active = useCallStore.getState().active
  if (!active) return
  const muted = !active.muted
  mic?.getAudioTracks().forEach((track) => {
    track.enabled = !muted
  })
  useCallStore.getState().patchActive({ muted })
}

// ─── Socket events ──────────────────────────────────────────────────

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

function endedMessage(call: CallSocketPayload): string {
  switch (call.status) {
    case 'completed':
      return `Call ended · ${formatDuration(call.duration ?? 0)}`
    case 'rejected':
      return 'Call declined.'
    case 'failed':
      return 'Call failed.'
    default:
      return call.direction === 'outbound' ? 'No answer.' : 'Call ended.'
  }
}

/** Entry point for `call.*` events from the notification socket. */
export function handleCallEvent(type: string, call: CallSocketPayload) {
  const store = useCallStore.getState()
  const isIncoming = store.incoming?.id === call.id
  const isActive = !!store.active && store.active.id === call.id

  switch (type) {
    case 'call.incoming': {
      if (!canAnswer || store.incoming || store.active || !call.sdp) return
      store.setIncoming({
        id: call.id,
        wabaId: String(call.waba_id ?? ''),
        customerId: call.customer_id ?? null,
        customerName: call.customer_name || call.wa_id || 'WhatsApp user',
        waId: call.wa_id ?? '',
        businessPhone: call.business_phone ?? '',
        sdp: call.sdp,
      })
      startRinging(call.id, () => {
        if (useCallStore.getState().incoming?.id === call.id) {
          stopRinging()
          useCallStore.getState().setIncoming(null)
        }
      })
      return
    }
    case 'call.claimed': {
      if (!isIncoming) return
      stopRinging()
      store.setIncoming(null)
      const me = useAuthStore.getState().user?.id
      if (call.agent_id !== me && call.agent_name) {
        toast(`${call.agent_name} answered the call from ${call.customer_name}.`)
      }
      return
    }
    case 'call.answer': {
      if (!call.sdp || !store.active || store.active.direction !== 'outbound') return
      if (isActive) {
        void applyAnswer(call.sdp)
      } else if (store.active.id === null) {
        pendingAnswer = { id: call.id, sdp: call.sdp }
      }
      return
    }
    case 'call.status': {
      if (!store.active || store.active.direction !== 'outbound' || !call.status) return
      if (isActive) applyOutboundStatus(call.status)
      else if (store.active.id === null) pendingStatus = { id: call.id, status: call.status }
      return
    }
    case 'call.ended': {
      // The finished call is now a row of Inbox → Calls; refetch it there.
      void import('@/lib/queryClient').then(({ queryClient }) =>
        queryClient.invalidateQueries({ queryKey: ['inbox-calls'] }),
      )
      if (isIncoming) {
        stopRinging()
        store.setIncoming(null)
      }
      if (isActive) {
        endActive()
        toast(endedMessage(call))
      }
      return
    }
  }
}
